import httpService from '../httpService';
import {
  ConnectAllShiftEventsResult,
  CreateScheduleBody,
  CreateShiftBody,
  ScheduleCommunityModel,
  ScheduleModel,
  ShiftCommunityAssignmentModel,
  ShiftEventConnection,
  ShiftEventConnectionRecommendation,
  UpdateScheduleCommunitiesBody,
  UpdateShiftCommunityAssignmentsBody,
  UpdateShiftEventConnectionBody,
  UpdateScheduleBody,
  UpdateShiftBody,
  UpdateShiftAttendanceBody,
  UpdateShiftAssignmentsBody,
} from '../../models/schedule.model';
import { Event, RecurrenceEditType } from '../../types/event';
import { store } from '../../redux/store';
import { RootState } from '../../redux/reducers';

function getCommunityId(): number {
  const { selectedCommunity } = (store.getState() as RootState).application;
  return selectedCommunity as number;
}

async function listSchedules(): Promise<ScheduleModel[]> {
  const communityId = getCommunityId();
  const { data } = await httpService.get(`/networks/communities/${communityId}/schedules`);
  return data;
}

async function createSchedule(body: CreateScheduleBody): Promise<ScheduleModel> {
  const communityId = getCommunityId();
  const { data } = await httpService.post(`/networks/communities/${communityId}/schedules`, body);
  return data;
}

async function updateSchedule(scheduleId: number, body: UpdateScheduleBody): Promise<ScheduleModel> {
  const communityId = getCommunityId();
  const { data } = await httpService.put(
    `/networks/communities/${communityId}/schedules/${scheduleId}`,
    body,
  );
  return data;
}

async function deleteSchedule(scheduleId: number): Promise<void> {
  const communityId = getCommunityId();
  await httpService.delete(`/networks/communities/${communityId}/schedules/${scheduleId}`);
}

async function listShifts(scheduleId: number): Promise<Event[]> {
  const communityId = getCommunityId();
  const { data } = await httpService.get(
    `/networks/communities/${communityId}/schedules/${scheduleId}/shifts`,
  );
  return data;
}

async function createShift(scheduleId: number, body: CreateShiftBody): Promise<Event> {
  const communityId = getCommunityId();
  const { data } = await httpService.post(
    `/networks/communities/${communityId}/schedules/${scheduleId}/shifts`,
    body,
  );
  return data;
}

async function updateShift(
  scheduleId: number,
  shiftEventId: number,
  body: UpdateShiftBody,
): Promise<Event> {
  const communityId = getCommunityId();
  const { data } = await httpService.put(
    `/networks/communities/${communityId}/schedules/${scheduleId}/shifts/${shiftEventId}`,
    body,
  );
  return data;
}

async function deleteShift(
  scheduleId: number,
  shiftEventId: number,
  deleteRecurrences: RecurrenceEditType = RecurrenceEditType.SINGLE,
): Promise<void> {
  const communityId = getCommunityId();
  await httpService.delete(
    `/networks/communities/${communityId}/schedules/${scheduleId}/shifts/${shiftEventId}?recurrence=${deleteRecurrences}`,
  );
}

async function setShiftAssignments(
  scheduleId: number,
  shiftEventId: number,
  body: UpdateShiftAssignmentsBody,
): Promise<Event> {
  const communityId = getCommunityId();
  const { data } = await httpService.put(
    `/networks/communities/${communityId}/schedules/${scheduleId}/shifts/${shiftEventId}/assignments`,
    body,
  );
  return data;
}

async function setShiftAssigneeAttendance(
  shiftEventId: number,
  participantId: number,
  body: UpdateShiftAttendanceBody,
  isExternal = false,
): Promise<void> {
  await httpService.put(`/events/${shiftEventId}/participants/${participantId}/attendance`, body, {
    params: { isExternal },
  });
}

async function listShiftEventConnectionRecommendations(
  scheduleId: number,
): Promise<ShiftEventConnectionRecommendation[]> {
  const communityId = getCommunityId();
  const { data } = await httpService.get(
    `/networks/communities/${communityId}/schedules/${scheduleId}/shifts/event-connections/recommendations`,
  );
  return data;
}

async function connectShiftToEvent(
  scheduleId: number,
  shiftEventId: number,
  body: UpdateShiftEventConnectionBody,
): Promise<ShiftEventConnection> {
  const communityId = getCommunityId();
  const { data } = await httpService.put(
    `/networks/communities/${communityId}/schedules/${scheduleId}/shifts/${shiftEventId}/event-connection`,
    body,
  );
  return data;
}

async function connectAllShiftsToRecommendedEvents(
  scheduleId: number,
): Promise<ConnectAllShiftEventsResult> {
  const communityId = getCommunityId();
  const { data } = await httpService.post(
    `/networks/communities/${communityId}/schedules/${scheduleId}/shifts/event-connections/connect-all-recommended`,
  );
  return data;
}

async function listScheduleCommunities(scheduleId: number): Promise<ScheduleCommunityModel[]> {
  const communityId = getCommunityId();
  const { data } = await httpService.get(
    `/networks/communities/${communityId}/schedules/${scheduleId}/communities`,
  );
  return data;
}

async function setScheduleCommunities(
  scheduleId: number,
  body: UpdateScheduleCommunitiesBody,
): Promise<ScheduleCommunityModel[]> {
  const communityId = getCommunityId();
  const { data } = await httpService.put(
    `/networks/communities/${communityId}/schedules/${scheduleId}/communities`,
    body,
  );
  return data;
}

async function listShiftCommunityAssignments(
  scheduleId: number,
  shiftEventId: number,
): Promise<ShiftCommunityAssignmentModel[]> {
  const communityId = getCommunityId();
  const { data } = await httpService.get(
    `/networks/communities/${communityId}/schedules/${scheduleId}/shifts/${shiftEventId}/community-assignments`,
  );
  return data;
}

async function setShiftCommunityAssignments(
  scheduleId: number,
  shiftEventId: number,
  body: UpdateShiftCommunityAssignmentsBody,
): Promise<ShiftCommunityAssignmentModel[]> {
  const communityId = getCommunityId();
  const { data } = await httpService.put(
    `/networks/communities/${communityId}/schedules/${scheduleId}/shifts/${shiftEventId}/community-assignments`,
    body,
  );
  return data;
}

export default {
  listSchedules,
  createSchedule,
  updateSchedule,
  deleteSchedule,
  listShifts,
  createShift,
  updateShift,
  deleteShift,
  setShiftAssignments,
  setShiftAssigneeAttendance,
  listShiftEventConnectionRecommendations,
  connectShiftToEvent,
  connectAllShiftsToRecommendedEvents,
  listScheduleCommunities,
  setScheduleCommunities,
  listShiftCommunityAssignments,
  setShiftCommunityAssignments,
};
